/**
 * Loan-contact search result → the contact fields the LOS stores.
 *
 * `POST /underwriting/search_loan_contacts` returns a search PARTY (guide §7):
 * a display name, the emails / phones / companies / licenses seen across loans
 * and Salesforce, and the roles it has played. A `ContactBase` on the loan is a
 * single person with single values, so a pick is a projection, not a copy —
 * first email, first phone, first license, and a role suggestion only when the
 * role is one the loan model can actually store.
 *
 * Shared so the add-contact sidebar and the Key-roles assign dialog project a
 * pick the same way.
 */
import { selectOptionsContactRole } from "../../constants/selectOptions/selectOptionsContactRole";

/** A firm result has no person name — the name IS the company. */
export const isFirmResult = (c) => c?.partyType === "contact_firm";

/** The role this party most often plays, if the loan model can store it. */
export const suggestedRoleOf = (c) =>
  (c?.roles || []).find((r) => selectOptionsContactRole.includes(r)) || null;

/**
 * The Salesforce CONTACT id behind a person, or null.
 *
 * `salesforceIds` is one sorted list holding both the party's Contact ids and
 * the Account ids of the firms linked to them, so it has to be filtered. A
 * Salesforce id's first three characters are its object's key prefix — a
 * platform-level constant, not per-org config — and `003` is Contact (`001` is
 * Account). That is the only thing in the payload that tells the two apart.
 *
 * A FIRM party is skipped outright: its ids are Accounts, and
 * `salesforceContactId` on a loan contact means a Contact.
 */
export const salesforceContactIdOf = (c) => {
  if (!c || isFirmResult(c)) return null;
  return (
    (c.salesforceIds || []).find((id) => String(id).startsWith("003")) || null
  );
};

export const mapLoanContactToContactValues = (c) => {
  const isFirm = isFirmResult(c);
  const name = (c?.displayName || "").trim();
  const [firstName, ...rest] = isFirm ? [""] : name.split(/\s+/);
  const suggestedRole = suggestedRoleOf(c);
  const sfContactId = salesforceContactIdOf(c);
  return {
    firstName: firstName || "",
    lastName: rest.join(" ") || "",
    emailAddress: c?.emails?.[0] || "",
    phone: c?.phones?.[0] || "",
    company: isFirm ? name : c?.companies?.[0] || "",
    appraiserLicenseNumber: c?.licenses?.[0] || "",
    ...(suggestedRole ? { loanRole: suggestedRole } : {}),
    // Carried so the saved contact still points back at the Salesforce record,
    // which is the one thing the retired "Import Salesforce Contact" path did
    // that this one did not.
    ...(sfContactId ? { salesforceContactId: sfContactId } : {}),
  };
};

export default mapLoanContactToContactValues;
